import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from './components/Header';
import AddressBlock from './components/AddressBlock';
import ProductCard from './components/ProductCard';
import Catalog from './components/catalog/Catalog';
import Favorites from './components/Favorites';
import Cart from './components/Cart';
import Profile from './components/Profile';
import ProductDetail from './components/ProductDetail';
import Register from './components/Register';
import Login from './components/Login';
import Footer from './components/Footer';
import InformationPage from './pages/InformationPage';
import { isInformationPath } from './data/informationPages';
import { apiClient } from './api/client';
import type { Product, User } from './api/types';
import { getProductImageUrl } from './utils/imageUrl';
import './App.css';

const fallbackProductImage = 'https://www.figma.com/api/mcp/asset/a35a8506-0e6b-4b02-9978-8aa45485e57f';
const PRODUCTS_PER_PAGE = 30;

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [favoriteProductIds, setFavoriteProductIds] = useState<Set<string>>(new Set());
  const observerTarget = useRef<HTMLDivElement>(null);

  const normalizedPath = useMemo(() => {
    if (location.pathname === '/') return '/';
    return location.pathname.replace(/\/+$/, '');
  }, [location.pathname]);

  const routeProductId = useMemo(() => {
    const match = normalizedPath.match(/^\/products\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : undefined;
  }, [normalizedPath]);

  const isFavoritesOpen = normalizedPath === '/favorites';
  const isCartOpen = normalizedPath === '/cart';
  const isProfileOpen = normalizedPath === '/profile';
  const isRegisterOpen = normalizedPath === '/register';
  const isLoginOpen = normalizedPath === '/login';
  const isProductDetailOpen = Boolean(routeProductId);
  const isSearchResultsOpen = normalizedPath === '/search';
  const isHomeOpen = normalizedPath === '/';

  useEffect(() => {
    const closeCatalog = () => setIsCatalogOpen(false);
    window.addEventListener('popstate', closeCatalog);
    return () => window.removeEventListener('popstate', closeCatalog);
  }, []);

  const appliedSearchQuery = (searchParams.get('q') || '').trim();
  const appliedCategoryId = searchParams.get('categoryId');
  const appliedCategoryName = (searchParams.get('categoryName') || '').trim();

  const getUserId = useCallback(() => currentUserId, [currentUserId]);

  const goHome = useCallback(() => {
    setIsCatalogOpen(false);
    setSearchInput('');
    navigate('/');
  }, [navigate]);

  const navigateToPath = useCallback((path: string) => {
    setIsCatalogOpen(false);
    navigate(path);
  }, [navigate]);

  const handleSearchSubmit = useCallback(() => {
    const normalizedQuery = searchInput.trim();
    setIsCatalogOpen(false);

    if (!normalizedQuery) {
      navigate('/');
      return;
    }

    const params = new URLSearchParams();
    params.set('q', normalizedQuery);
    navigate(`/search?${params.toString()}`);
  }, [navigate, searchInput]);

  useEffect(() => {
    if (isSearchResultsOpen) {
      setSearchInput(appliedSearchQuery);
    } else if (isHomeOpen) {
      setSearchInput('');
    }
  }, [appliedSearchQuery, isHomeOpen, isSearchResultsOpen]);

  useEffect(() => {
    const validateStoredUser = async () => {
      const storedUserId = localStorage.getItem('userId');
      if (!storedUserId) {
        setCurrentUserId(null);
        return;
      }

      try {
        const response = await apiClient.getUser(storedUserId);
        if (response.success && response.data) {
          setCurrentUserId(storedUserId);
          return;
        }
      } catch (err) {
        console.error('Stored user validation failed:', err);
      }

      localStorage.removeItem('userId');
      setCurrentUserId(null);
      setFavoriteProductIds(new Set());
    };

    void validateStoredUser();
  }, []);

  const handleRegistrationSuccess = useCallback((user: User) => {
    setCurrentUserId(user.id);
    localStorage.setItem('userId', user.id);
    setIsCatalogOpen(false);
    alert(`Регистрация прошла успешно! Добро пожаловать, ${user.display_name || user.first_name || 'Пользователь'}!`);
    navigate('/profile');
  }, [navigate]);

  const handleLoginSuccess = useCallback((user: User) => {
    setCurrentUserId(user.id);
    localStorage.setItem('userId', user.id);
    setIsCatalogOpen(false);
    alert(`Добро пожаловать, ${user.display_name || user.first_name || 'Пользователь'}!`);
    navigate('/profile');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setCurrentUserId(null);
    localStorage.removeItem('userId');
    setFavoriteProductIds(new Set());
    setIsCatalogOpen(false);
    alert('Вы вышли из системы');
    navigate('/');
  }, [navigate]);

  const handleProductCardFavoriteToggle = useCallback(async (productId: string, isFavorite: boolean) => {
    const userId = getUserId();
    if (!userId) {
      alert('Необходимо войти в систему для добавления в избранное');
      return;
    }

    try {
      if (isFavorite) {
        const response = await apiClient.addToFavorites(userId, productId);
        if (!response.success) {
          throw new Error(response.error || 'Failed to add to favorites');
        }
        setFavoriteProductIds((prev) => new Set([...prev, productId]));
      } else {
        const response = await apiClient.removeFromFavorites(userId, productId);
        if (!response.success) {
          throw new Error(response.error || 'Failed to remove from favorites');
        }
        setFavoriteProductIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить статус избранного');
    }
  }, [getUserId]);

  useEffect(() => {
    const loadFavorites = async () => {
      const userId = getUserId();
      if (!userId) {
        setFavoriteProductIds(new Set());
        return;
      }

      try {
        const favoritesResponse = await apiClient.getUserFavorites(userId);
        if (favoritesResponse.success && favoritesResponse.data) {
          setFavoriteProductIds(new Set(favoritesResponse.data));
          return;
        }

        if (!favoritesResponse.success && /404|not found|не найден/i.test(favoritesResponse.error || '')) {
          localStorage.removeItem('userId');
          setCurrentUserId(null);
          setFavoriteProductIds(new Set());
        }
      } catch (err) {
        console.error('Error loading favorites:', err);
      }
    };

    void loadFavorites();
  }, [currentUserId, getUserId]);

  const loadProducts = useCallback(async (
    page: number,
    append = false,
    query = '',
    categoryId: string | null = null,
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
    }

    try {
      const productsResponse = await apiClient.getProducts({
        page,
        limit: PRODUCTS_PER_PAGE,
        active_only: true,
        status: 'published',
        q: query || undefined,
        category_id: categoryId || undefined,
      });

      if (!productsResponse.success || !productsResponse.data) {
        throw new Error(productsResponse.error || 'Failed to load products');
      }

      const newProducts = productsResponse.data;

      if (import.meta.env.DEV) {
        console.log('Loaded products:', newProducts.length);
      }

      if (append) {
        setProducts((prev) => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }

      const pagination = productsResponse.pagination;
      if (pagination) {
        setHasMore(Boolean(pagination.has_next));
      } else {
        setHasMore(newProducts.length === PRODUCTS_PER_PAGE);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      console.error('Error loading products:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!isHomeOpen && !isSearchResultsOpen) {
      return;
    }

    const query = isSearchResultsOpen ? appliedSearchQuery : '';
    const categoryId = isSearchResultsOpen ? appliedCategoryId : null;

    setCurrentPage(1);
    setHasMore(true);
    void loadProducts(1, false, query, categoryId);
  }, [isHomeOpen, isSearchResultsOpen, appliedSearchQuery, appliedCategoryId, loadProducts]);

  useEffect(() => {
    if (!isHomeOpen && !isSearchResultsOpen) {
      return;
    }

    const currentTarget = observerTarget.current;
    if (!currentTarget) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || !hasMore || isLoading || isLoadingMore) {
          return;
        }

        const nextPage = currentPage + 1;
        const query = isSearchResultsOpen ? appliedSearchQuery : '';
        const categoryId = isSearchResultsOpen ? appliedCategoryId : null;

        setCurrentPage(nextPage);
        void loadProducts(nextPage, true, query, categoryId);
      },
      { threshold: 0.1 },
    );

    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
      observer.disconnect();
    };
  }, [
    currentPage,
    hasMore,
    isHomeOpen,
    isLoading,
    isLoadingMore,
    isSearchResultsOpen,
    appliedSearchQuery,
    appliedCategoryId,
    loadProducts,
  ]);

  const openProductDetail = useCallback((productId: string) => {
    setIsCatalogOpen(false);
    navigate(`/products/${encodeURIComponent(productId)}`);
  }, [navigate]);

  const renderProductsGrid = (endText: string) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-[20px] text-gray-600">Загрузка товаров...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-[20px] text-red-600">Ошибка загрузки: {error}</div>
        </div>
      );
    }

    if (products.length === 0) {
      return null;
    }

    return (
      <>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] sm:gap-5 xl:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] xl:gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className="cursor-pointer"
              onClick={() => openProductDetail(product.id)}
            >
              <ProductCard
                image={getProductImageUrl(product.primary_image, fallbackProductImage)}
                title={product.name}
                price={product.price}
                productId={product.id}
                isFavorite={favoriteProductIds.has(product.id)}
                onFavoriteToggle={handleProductCardFavoriteToggle}
              />
            </div>
          ))}
        </div>

        {hasMore && (
          <div ref={observerTarget} className="flex h-20 w-full items-center justify-center">
            {isLoadingMore && <div className="text-[16px] text-gray-600">Загрузка товаров...</div>}
          </div>
        )}

        {!hasMore && products.length > 0 && (
          <div className="flex w-full items-center justify-center py-10">
            <div className="text-[16px] text-gray-500">{endText}</div>
          </div>
        )}
      </>
    );
  };

  const renderMainContent = () => {
    if (!isHomeOpen) {
      return null;
    }

    return (
      <main className="relative mx-auto w-full max-w-[1520px] flex-1 px-4 pt-[20px] sm:px-6 lg:px-8">
        <div className="mb-[20px]">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="h-[120px] rounded-[20px] bg-gradient-to-r from-[#b8dae9] from-[41.258%] to-[#d8aaff] sm:h-[140px] lg:h-[160px]" />
            {getUserId() && <AddressBlock userId={getUserId()} className="w-full" />}
          </div>
        </div>

        <div>
          {products.length === 0 && !isLoading && !error ? (
            <div className="flex min-h-[320px] items-center justify-center py-20">
              <div className="text-[20px] text-gray-600">Товары не найдены</div>
            </div>
          ) : (
            renderProductsGrid('Все товары загружены')
          )}
        </div>
      </main>
    );
  };

  const renderSearchResults = () => {
    if (!isSearchResultsOpen) {
      return null;
    }

    return (
      <main className="relative mx-auto flex-1 w-full max-w-[1520px] px-4 pt-[20px] sm:px-6 lg:px-8">
        <div>
          <div className="mb-8">
            <div className="text-[28px] font-bold text-black sm:text-[34px]">Результаты поиска</div>
            <div className="mt-2 text-[16px] text-gray-600 sm:text-[18px]">
              {appliedCategoryId
                ? `Категория: "${appliedCategoryName}"`
                : `По запросу "${appliedSearchQuery}"`}
            </div>
          </div>

          {products.length === 0 && !isLoading && !error ? (
            <div className="flex min-h-[280px] items-center justify-center py-16 sm:min-h-[320px] sm:py-20">
              <div className="px-4 text-center text-[18px] text-gray-600 sm:text-[20px]">
                {appliedCategoryId
                  ? `В категории "${appliedCategoryName}" ничего не найдено`
                  : `По запросу "${appliedSearchQuery}" ничего не найдено`}
              </div>
            </div>
          ) : (
            renderProductsGrid('Все результаты загружены')
          )}
        </div>
      </main>
    );
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-[#f3f3f3]">
      <Header
        isCatalogOpen={isCatalogOpen}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearchSubmit}
        onCatalogToggle={() => setIsCatalogOpen((prev) => !prev)}
        onLogoClick={goHome}
        onFavoritesClick={() => navigateToPath('/favorites')}
        onCartClick={() => navigateToPath('/cart')}
        onProfileClick={() => navigateToPath('/profile')}
      />

      <div className="flex flex-1 flex-col">
        {isCatalogOpen && (
          <Catalog
            onClose={() => setIsCatalogOpen(false)}
            onSelectCategory={(category) => {
              const params = new URLSearchParams();
              params.set('categoryId', category.id);
              params.set('categoryName', category.name);
              setSearchInput('');
              setIsCatalogOpen(false);
              navigate(`/search?${params.toString()}`);
            }}
          />
        )}

        {isFavoritesOpen && (
          <Favorites
            userId={getUserId()}
            onProductClick={openProductDetail}
            onFavoriteRemoved={(productId) => {
              setFavoriteProductIds((prev) => {
                const next = new Set(prev);
                next.delete(productId);
                return next;
              });
            }}
          />
        )}

        {isCartOpen && <Cart userId={getUserId()} />}

        {isLoginOpen && (
          <Login
            onLogoClick={goHome}
            onSuccess={handleLoginSuccess}
            onCancel={goHome}
            onRegisterClick={() => navigateToPath('/register')}
          />
        )}

        {isRegisterOpen && (
          <Register
            onLogoClick={goHome}
            onSuccess={handleRegistrationSuccess}
            onCancel={goHome}
            onLoginClick={() => navigateToPath('/login')}
          />
        )}

        {isProfileOpen && (
          <Profile
            userId={getUserId()}
            onLogout={handleLogout}
            onLoginClick={() => navigateToPath('/login')}
            onRegisterClick={() => navigateToPath('/register')}
          />
        )}

        {isProductDetailOpen && (
          <ProductDetail
            productId={routeProductId}
            userId={getUserId() ?? undefined}
            onBack={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/');
              }
            }}
          />
        )}

        {renderMainContent()}
        {renderSearchResults()}
        {isInformationPath(normalizedPath) && <InformationPage path={normalizedPath} />}
      </div>

      <Footer />
    </div>
  );
}

export default App;
